// SAFE-WRITE-BEGIN: frontend/src/pages/valuation/_ValueMethodsSection.tsx
/**
 * M-DEMO — Compare values + the 10-year Forecast, as one block under the
 * headline value (owner step 5) and on the signer's Methodology screen.
 *
 * The owner may set three things here and nothing else (P7a): cash, debt and
 * the forecast rate. Each goes through the existing draft PUT as ONLY
 * `tuning.value_methods.<field>` — never `chosen`, never a basis, rate or
 * override — and then the page recomputes. SCREEN ONLY: nothing here reaches
 * the Snapshot, a report or the signed record.
 */
import { useEffect, useId, useState } from 'react'
import { saveValuationDraft } from '@/api/valuationB3'
import { formatApiError } from '@/utils/httpErrors'
import type { ValueMethodsPayload } from '@/types/valuationB3'
import { CompareValues } from './_CompareValues'
import { ValueForecast } from './_ValueForecast'
import { MD, storedNumber } from './_valueMethods'

type Field = 'cash' | 'debt' | 'forecast_growth_rate'

function errorCode(err: unknown): string | undefined {
  return (err as { response?: { data?: { error?: { code?: string } } } })?.response?.data?.error?.code
}

function MoneyBox({ label, saved, onSave }: {
  label: string
  saved: number | null
  onSave: (v: number | null) => void
}) {
  const id = useId()
  const [text, setText] = useState(saved == null ? '' : String(saved))
  useEffect(() => { setText(saved == null ? '' : String(saved)) }, [saved])

  const blur = () => {
    const s = text.trim()
    const next = s === '' ? null : Number(s)
    if (next !== null && !Number.isFinite(next)) return
    if (next === saved) return
    onSave(next)
  }

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-pal-ink-body">{label}</label>
      <input
        id={id}
        type="number"
        min={0}
        step={1000}
        value={text}
        onChange={e => setText(e.target.value)}
        onBlur={blur}
        className="w-full rounded border border-pal-line-strong px-3 py-2"
      />
    </div>
  )
}

export function ValueMethodsSection({ engagementId, payload, signer, stored, onSaved }: {
  engagementId: string
  payload: ValueMethodsPayload
  /** A valuation signer sees every value the server sent; anyone else only the available ones. */
  signer: boolean
  /** The saved `tuning.value_methods` (draftExtras), for the cash and debt boxes. */
  stored: Record<string, unknown> | null | undefined
  onSaved: () => void
}) {
  const [saved, setSaved] = useState(() => ({
    cash: storedNumber(stored?.cash),
    debt: storedNumber(stored?.debt),
  }))
  const storedCash = storedNumber(stored?.cash)
  const storedDebt = storedNumber(stored?.debt)
  useEffect(() => { setSaved({ cash: storedCash, debt: storedDebt }) }, [storedCash, storedDebt])

  const [rateRefused, setRateRefused] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const save = async (field: Field, value: number | null) => {
    setSaveError(null)
    if (field === 'forecast_growth_rate') setRateRefused(false)
    try {
      await saveValuationDraft(engagementId, { tuning: { value_methods: { [field]: value } } })
    } catch (err) {
      if (errorCode(err) === 'FORECAST_GROWTH_OUT_OF_RANGE') setRateRefused(true)
      else setSaveError(formatApiError(err))
      return
    }
    if (field !== 'forecast_growth_rate') setSaved(s => ({ ...s, [field]: value }))
    onSaved()
  }

  const enterprise = payload.methods.enterprise
  const today = enterprise?.status === 'available' ? enterprise.value : null

  return (
    <div className="space-y-6">
      <CompareValues
        methods={payload.methods}
        signer={signer}
        inputs={
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <MoneyBox label={MD.cash} saved={saved.cash} onSave={v => void save('cash', v)} />
            <MoneyBox label={MD.debt} saved={saved.debt} onSave={v => void save('debt', v)} />
          </div>
        }
      />
      {saveError && <p className="text-sm text-pal-warning-text">{saveError}</p>}
      {today != null && (
        <ValueForecast
          today={today}
          initialRate={payload.forecast.growth_rate}
          error={rateRefused}
          onCommit={rate => void save('forecast_growth_rate', rate)}
        />
      )}
    </div>
  )
}
// SAFE-WRITE-EOF: frontend/src/pages/valuation/_ValueMethodsSection.tsx
